class CommunityCarousel extends HTMLElement {
  constructor() {
    super();
    
    this.wrapper = this.querySelector('.community-carousel__carousel-wrapper');
    this.track = this.querySelector('.community-carousel__carousel-track');
    this.slides = this.querySelectorAll('.community-carousel__slide');
    this.prevBtn = this.querySelector('.community-carousel__nav-btn--prev');
    this.nextBtn = this.querySelector('.community-carousel__nav-btn--next');
    this.btn = this.querySelector('.community-carousel__action .mt-btn');
    this.header = this.querySelector('.community-carousel__header');
    
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = Number(this.dataset.speed) || 1;
    this.muted = this.dataset.muted === 'true';
    
    this.position = 0;
    this.autoplaySpeed = -0.5 * this.speed;
    this.isDragging = false;
    this.startPos = 0;
    this.startPosY = 0;
    this.animationFrameId = null;
    this.trackWidth = 0;
    this.videosPreloaded = false;
    this.activeVideo = null;
    
    this.animate = this.animate.bind(this);
    this.dragStart = this.dragStart.bind(this);
    this.dragMove = this.dragMove.bind(this);
    this.dragEnd = this.dragEnd.bind(this);
    this.handleVideoClick = this.handleVideoClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleNavClick = this.handleNavClick.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (!this.track || this.track.children.length === 0) return;

    this.duplicateSlides();
    this.calculateTrackWidth();
    this.attachEventListeners();
    
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  duplicateSlides() {
    const originalSlides = [...this.track.children];
    originalSlides.forEach(slide => {
      const clone = slide.cloneNode(true);
      this.track.appendChild(clone);
    });
  }

  calculateTrackWidth() {
    const firstSlide = this.track.children[0];
    if (!firstSlide) return;
    
    const slideWidth = firstSlide.offsetWidth;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    const slideCount = this.slides.length;
    
    this.trackWidth = (slideWidth + gap) * slideCount;
  }

  attachEventListeners() {
    // Drag events
    this.track.addEventListener('mousedown', this.dragStart);
    this.track.addEventListener('touchstart', this.dragStart, { passive: false });
    window.addEventListener('mousemove', this.dragMove);
    window.addEventListener('touchmove', this.dragMove, { passive: false });
    window.addEventListener('mouseup', this.dragEnd);
    window.addEventListener('touchend', this.dragEnd);

    // Video click events
    this.addEventListener('click', this.handleVideoClick);

    // Navigation buttons
    if (this.prevBtn && this.nextBtn) {
      this.prevBtn.addEventListener('click', () => this.handleNavClick('prev'));
      this.nextBtn.addEventListener('click', () => this.handleNavClick('next'));
    }

    // Scroll events for video preloading and pausing
    window.addEventListener('scroll', this.handleScroll);

    // CTA button tracking
    if (this.btn && this.header) {
      this.btn.addEventListener('click', () => {
        if (window.app?.dl?.ctaClick) {
          window.app.dl.ctaClick(
            this.btn.querySelector('.mt-btn__inner')?.textContent || '',
            { location: this.header.textContent || '' }
          );
        }
      });
    }
  }

  startAutoplay() {
    if (this.animationFrameId) return;
    this.animate();
  }

  stopAutoplay() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  animate() {
    if (!this.isDragging && this.autoplay) {
      this.position += this.autoplaySpeed;
      
      // Loop seamlessly
      if (this.position < -this.trackWidth) {
        this.position += this.trackWidth;
      }
      if (this.position > 0) {
        this.position -= this.trackWidth;
      }
    }
    
    this.track.style.transform = `translateX(${this.position}px)`;
    this.animationFrameId = requestAnimationFrame(this.animate);
  }

  dragStart(e) {
    this.isDragging = true;
    this.track.classList.add('grabbing');
    
    const clientX = e.pageX || e.touches?.[0]?.pageX || 0;
    const clientY = e.pageY || e.touches?.[0]?.pageY || 0;
    
    this.startPos = clientX - this.position;
    this.startPosY = clientY;
    this.lastPos = clientX;
    
    if (this.autoplay) {
      this.stopAutoplay();
    }
  }

  dragMove(e) {
    if (!this.isDragging) return;
    
    const clientX = e.pageX || e.touches?.[0]?.pageX || 0;
    const clientY = e.pageY || e.touches?.[0]?.pageY || 0;
    
    const deltaX = Math.abs(clientX - (this.startPos + this.position));
    const deltaY = Math.abs(clientY - this.startPosY);
    
    // Prevent scroll only if horizontal drag is dominant
    if (deltaX > deltaY && deltaX > 5) {
      e.preventDefault();
      this.position = clientX - this.startPos;
      
      // Loop seamlessly during drag
      if (this.position < -this.trackWidth) {
        this.position += this.trackWidth;
        this.startPos -= this.trackWidth;
      }
      if (this.position > 0) {
        this.position -= this.trackWidth;
        this.startPos += this.trackWidth;
      }
      
      this.track.style.transform = `translateX(${this.position}px)`;
    }
  }

  dragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.track.classList.remove('grabbing');
    
    if (this.autoplay && !this.activeVideo) {
      this.startAutoplay();
    }
  }

  handleNavClick(direction) {
    const slideWidth = this.track.children[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    const step = slideWidth + gap;
    
    if (direction === 'next') {
      this.position -= step;
    } else {
      this.position += step;
    }
    
    // Ensure looping
    if (this.position < -this.trackWidth) {
      this.position += this.trackWidth;
    }
    if (this.position > 0) {
      this.position -= this.trackWidth;
    }
    
    this.track.style.transform = `translateX(${this.position}px)`;
  }

  handleVideoClick(e) {
    const overlay = e.target.closest('.community-carousel__video-overlay');
    if (!overlay) return;
    
    const slide = overlay.closest('.community-carousel__slide');
    const video = slide.querySelector('.community-carousel__video-player');
    
    if (!video) return;
    
    const isPlaying = !video.paused && !video.ended && video.readyState > 2;
    
    if (isPlaying) {
      video.pause();
      slide.classList.remove('active');
      this.activeVideo = null;
      
      if (this.autoplay) {
        this.startAutoplay();
      }
    } else {
      // Pause any currently playing video
      if (this.activeVideo && this.activeVideo !== video) {
        this.activeVideo.pause();
        this.activeVideo.closest('.community-carousel__slide')?.classList.remove('active');
      }
      
      video.play();
      slide.classList.add('active');
      this.activeVideo = video;
      
      if (this.autoplay) {
        this.stopAutoplay();
      }
    }
    
    // Analytics tracking
    if (window.app?.dl?.customClick) {
      const slideIndex = [...this.querySelectorAll('.community-carousel__slide')].indexOf(slide) + 1;
      window.app.dl.customClick('', {
        type: 'video',
        location: this.header?.textContent || 'Community',
        position: slideIndex
      });
    }
  }

  handleScroll() {
    // Pause videos when section is out of viewport
    if (this.isElementOutOfViewport(this)) {
      const playingVideos = this.querySelectorAll('.community-carousel__video-player');
      playingVideos.forEach(video => {
        if (!video.paused) {
          video.pause();
          video.closest('.community-carousel__slide')?.classList.remove('active');
        }
      });
      
      this.activeVideo = null;
      
      if (this.autoplay) {
        this.startAutoplay();
      }
    }
    
    // Preload videos when section is near viewport
    if (!this.videosPreloaded && this.isNearViewport(this)) {
      this.preloadVideos();
      this.videosPreloaded = true;
    }
  }

  preloadVideos() {
    const videos = [...this.querySelectorAll('.community-carousel__video-player')];
    
    videos.forEach((video, index) => {
      setTimeout(() => {
        video.preload = 'metadata';
      }, index * 500); // Stagger preloading
    });
  }

  isElementOutOfViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.bottom < 0 ||
      rect.right < 0 ||
      rect.left > window.innerWidth ||
      rect.top > window.innerHeight
    );
  }

  isNearViewport(el) {
    const rect = el.getBoundingClientRect();
    const threshold = window.innerHeight * 1.5;
    return rect.top < threshold;
  }

  cleanup() {
    this.stopAutoplay();
    
    window.removeEventListener('mousemove', this.dragMove);
    window.removeEventListener('touchmove', this.dragMove);
    window.removeEventListener('mouseup', this.dragEnd);
    window.removeEventListener('touchend', this.dragEnd);
    window.removeEventListener('scroll', this.handleScroll);
  }
}

customElements.define('community-carousel', CommunityCarousel);